import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D45fromD1 = () => {
  const title = "Calculation of D45 (Akshavedamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D45 chart, or Akshavedamsa chart, is used in Vedic astrology to
        analyze inherited traits, paternal lineage, and challenges or blessings
        that arise from the father's side of the family. Below is the
        step-by-step process to calculate the D45 chart from the D1 (Rasi)
        chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D45 (Akshavedamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mercury is located at 18° Virgo, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Forty-Five Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into forty-five equal parts, each spanning
        0.6667°. To find the Akshavedamsa position of a planet, we divide its
        degree in the sign by 0.6667.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Akshavedamsa \ Position = \frac{Degree \ in \ Sign}{0.6667}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{0.6667} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 0.6667°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{0.6667} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 0.6667°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{0.6667} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 0.6667°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 45 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Akshavedamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 0.6667°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mercury at 18° Virgo
      </Typography>

      <Typography variant="body1" paragraph>
        Virgo is the D1 sign. Mercury at 18° falls in the twenty-seventh 0.6667°
        (between 18° and 18.6667°). Therefore, it belongs to the{" "}
        <strong>27th sign</strong> from Virgo, which is <strong>Pisces</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D45 (Akshavedamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D45
        (Akshavedamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_akshavedamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Akshavedamsa the planet is in based on its degree
    if degree <= 0.6667:
        # First 0.6667° belongs to the same sign
        akshavedamsa_sign = zodiac_signs[sign_index]
    elif 0.6667 < degree <= 1.3334:
        # Second 0.6667° belongs to the 2nd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 1.3334 < degree <= 2:
        # Third 0.6667° belongs to the 3rd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 2 < degree <= 2.6667:
        # Fourth 0.6667° belongs to the 4th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 2.6667 < degree <= 3.3334:
        # Fifth 0.6667° belongs to the 5th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 3.3334 < degree <= 4:
        # Sixth 0.6667° belongs to the 6th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 4 < degree <= 4.6667:
        # Seventh 0.6667° belongs to the 7th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 4.6667 < degree <= 5.3334:
        # Eighth 0.6667° belongs to the 8th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 5.3334 < degree <= 6:
        # Ninth 0.6667° belongs to the 9th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 6 < degree <= 6.6667:
        # Tenth 0.6667° belongs to the 10th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 6.6667 < degree <= 7.3334:
        # Eleventh 0.6667° belongs to the 11th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 7.3334 < degree <= 8:
        # Twelfth 0.6667° belongs to the 12th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 8 < degree <= 8.6667:
        # Thirteenth 0.6667° belongs to the 13th sign from the current sign
        akshavedamsa_sign = zodiac_signs[sign_index]
    elif 8.6667 < degree <= 9.3334:
        # Fourteenth 0.6667° belongs to the 14th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 9.3334 < degree <= 10:
        # Fifteenth 0.6667° belongs to the 15th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 10 < degree <= 10.6667:
        # Sixteenth 0.6667° belongs to the 16th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 10.6667 < degree <= 11.3334:
        # Seventeenth 0.6667° belongs to the 17th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 11.3334 < degree <= 12:
        # Eighteenth 0.6667° belongs to the 18th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 12 < degree <= 12.6667:
        # Nineteenth 0.6667° belongs to the 19th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 12.6667 < degree <= 13.3334:
        # Twentieth 0.6667° belongs to the 20th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 13.3334 < degree <= 14:
        # Twenty-first 0.6667° belongs to the 21st sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 14 < degree <= 14.6667:
        # Twenty-second 0.6667° belongs to the 22nd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 14.6667 < degree <= 15.3334:
        # Twenty-third 0.6667° belongs to the 23rd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 15.3334 < degree <= 16:
        # Twenty-fourth 0.6667° belongs to the 24th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 16 < degree <= 16.6667:
        # Twenty-fifth 0.6667° belongs to the 25th sign from the current sign
        akshavedamsa_sign = zodiac_signs[sign_index]
    elif 16.6667 < degree <= 17.3334:
        # Twenty-sixth 0.6667° belongs to the 26th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 17.3334 < degree <= 18:
        # Twenty-seventh 0.6667° belongs to the 27th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 18 < degree <= 18.6667:
        # Twenty-eighth 0.6667° belongs to the 28th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 18.6667 < degree <= 19.3334:
        # Twenty-ninth 0.6667° belongs to the 29th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 19.3334 < degree <= 20:
        # Thirtieth 0.6667° belongs to the 30th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 20 < degree <= 20.6667:
        # Thirty-first 0.6667° belongs to the 31st sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 20.6667 < degree <= 21.3334:
        # Thirty-second 0.6667° belongs to the 32nd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 21.3334 < degree <= 22:
        # Thirty-third 0.6667° belongs to the 33rd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 22 < degree <= 22.6667:
        # Thirty-fourth 0.6667° belongs to the 34th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 22.6667 < degree <= 23.3334:
        # Thirty-fifth 0.6667° belongs to the 35th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 23.3334 < degree <= 24:
        # Thirty-sixth 0.6667° belongs to the 36th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 24 < degree <= 24.6667:
        # Thirty-seventh 0.6667° belongs to the 37th sign from the current sign
        akshavedamsa_sign = zodiac_signs[sign_index]
    elif 24.6667 < degree <= 25.3334:
        # Thirty-eighth 0.6667° belongs to the 38th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 25.3334 < degree <= 26:
        # Thirty-ninth 0.6667° belongs to the 39th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 26 < degree <= 26.6667:
        # Fortieth 0.6667° belongs to the 40th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 26.6667 < degree <= 27.3334:
        # Forty-first 0.6667° belongs to the 41st sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 27.3334 < degree <= 28:
        # Forty-second 0.6667° belongs to the 42nd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 28 < degree <= 28.6667:
        # Forty-third 0.6667° belongs to the 43rd sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 28.6667 < degree <= 29.3334:
        # Forty-fourth 0.6667° belongs to the 44th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    else:
        # Forty-fifth 0.6667° belongs to the 45th sign from the current sign
        akshavedamsa_sign = zodiac_signs[(sign_index + 8) % 12]

    return akshavedamsa_sign

# Example usage
planet = "Mercury"
degree = 18
sign = "Virgo"

akshavedamsa_sign = calculate_akshavedamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {akshavedamsa_sign} in the D45 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Akshavedamsa for a planet based
        on its degree and sign in the D1 chart. The program divides the sign
        into forty-five equal parts and assigns the Akshavedamsa based on the
        planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into forty-five parts: the first 0.6667°,
        the second 0.6667°, and so on. It assigns the Akshavedamsa based on the
        planet's degree within the sign:
        <br />
        - First 0.6667° belongs to the same sign.
        <br />
        - Second 0.6667° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 0.6667° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 0.6667° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 0.6667° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 0.6667° belongs to the 6th sign from the D1 sign.
        <br />
        - Seventh 0.6667° belongs to the 7th sign from the D1 sign.
        <br />
        - Eighth 0.6667° belongs to the 8th sign from the D1 sign.
        <br />
        - Ninth 0.6667° belongs to the 9th sign from the D1 sign.
        <br />- Tenth 0.6667° belongs to the 10th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D45fromD1;
